import React, { memo, ReactNode, useCallback, useMemo } from "react";
import colors from "app/colors";
import { Text, HStack, VStack } from "@chakra-ui/react";
import { SearchFilter, SearchFilterType } from "gql/graphql";
import UserTagGroupSelector, {
  Option,
} from "modules/selectors/user-tag-group-selector";
import { useUsers } from "modules/providers/users-provider";

interface IProps {
  newSearchFilters: SearchFilter[];
  setNewSearchFilters: React.Dispatch<React.SetStateAction<SearchFilter[]>>;
  title: string;
  type: SearchFilterType;
  icon: ReactNode;
}

const FiltersDrawerThreadUserSelector = ({
  newSearchFilters,
  setNewSearchFilters,
  title,
  type,
  icon,
}: IProps) => {
  const { usersById } = useUsers();

  const getOptionValues = useCallback(
    (_type: SearchFilterType): Option[] => {
      const filter = newSearchFilters.find((filter) => filter.type === type);
      if (!filter || !filter.objectIdListValue) return [];
      if (_type === type) {
        return filter.objectIdListValue.map((id) => ({
          id,
          type: "user",
          active: usersById[id]?.active ?? false,
        }));
      }
      return [];
    },
    [newSearchFilters, type, usersById],
  );

  const _options = useMemo<Option[]>(
    () => [...getOptionValues(type)],
    [getOptionValues, type],
  );

  const onSelectedUsersChanged = useCallback(
    (options: Option[]) => {
      const users = options.filter((option) => option.type === "user");

      setNewSearchFilters((prev) => {
        const _prev = [...prev].filter((filter) => filter.type !== type);
        if (users.length > 0) {
          _prev.push({
            type,
            objectIdListValue: users.map((user) => user.id),
          });
        }
        return _prev;
      });
    },
    [setNewSearchFilters, type],
  );

  return (
    <VStack w="full">
      <HStack w="full">
        {icon}
        <Text
          fontFamily="Outfit"
          color={colors.blackSecondary}
          fontSize={18}
          fontWeight={600}
        >
          {title}
        </Text>
      </HStack>
      <UserTagGroupSelector
        types={["user"]}
        isMultiSelect
        onSelectedOptionsChanged={onSelectedUsersChanged}
        allowInactive
        value={_options}
        placeHolder="@ Enter user"
      />
    </VStack>
  );
};

export default memo(FiltersDrawerThreadUserSelector);
